import React, { useImperativeHandle, useState } from 'react';
import Modal from '@app/component/overlays/modal/Modal';
import useModal from '@app/component/overlays/modal/ModalRef';
import _isEmpty from 'lodash/isEmpty';
import Rating from '@app/component/rating/Rating';
import Utilities from '@util/Utilities';
import SearchHotelResult from '@app/component/form/input/autocomplete/SearchHotelResult';
import { useForm } from 'react-hook-form';
import FormButton from '@app/component/form/button/FormButton';
import FormPlaceholder from '@app/component/placeholder/FormPlaceholder';
import Message from '@app/component/message/Message';
import { RadioGroup } from '@headlessui/react';
import _capitalize from 'lodash/capitalize';
import { HotelSuggestionsOutput } from '@app/model/hotel/HotelSuggestionsOutput';
import { HotelReference } from '@app/model/hotel/HotelReference';
import HotelService from '@app/model/hotel/service/HotelService';


enum TAB {
	STARS = 'STARS', KEYS = 'KEYS', ALL = 'ALL'
}

const SetupPropertyHotelCompetitorSuggestionsModal: React.FunctionComponent<{
	onSave: (competitors: SearchHotelResult[]) => any,
	onClose?: () => any,
	ref: any
}>
	= React.forwardRef(({ onSave, onClose }, ref: React.Ref<any>) => {

	const modal = useModal();
	const [hotel, setHotel] = useState<SearchHotelResult>();
	const [hotelSuggestionsOutput, setHotelSuggestionsOutput] = useState<HotelSuggestionsOutput>();
	const [isLoading, setIsLoading] = useState(true);
	const form = useForm();
	const [filter, setFilter] = useState<TAB>(TAB.STARS);


	useImperativeHandle(ref, () => {
		return {
			open: (hotelId: number) => {
				setHotel(hotel);
				form.reset({ competitorIds: [] });
				HotelService.findHotelSuggestions(hotelId)
					.then(d => setHotelSuggestionsOutput(d))
					.finally(() => setIsLoading(false));
				modal.open();
			},
		};
	});

	const onAdd = (data: any) => {
		if (!_isEmpty(data.competitorIds) && !!hotelSuggestionsOutput && !_isEmpty(hotelSuggestionsOutput.suggestions)) {
			const competitors: SearchHotelResult[] = [];
			data.competitorIds.forEach((cId: number) => {
				const item: HotelReference | undefined = hotelSuggestionsOutput.suggestions.find(c => c.hotel.id == cId);
				if (!!item) {
					competitors.push({
						id: item.hotel.id,
						name: item.hotel.name,
						address: item.hotel.location!.address,
						countryName: item.hotel.location!.country.name,
						coordinates: item.hotel.location!.coordinates,
					});
				}
			});
			onSave(competitors);
			modal.close();
		}
	};

	return <Modal ref={modal.ref} onClose={onClose} title="Competitors Suggestion" width="2xlarge" actions={
		!!hotelSuggestionsOutput && !_isEmpty(hotelSuggestionsOutput.suggestions) && !isLoading &&
		<FormButton state="primary" onClick={form.handleSubmit(onAdd)}>
			Add Competitors
		</FormButton>
	}>
		{!!hotel &&
			<p className="text-sm text-gray-500">For your property <strong className="font-medium text-gray-800">
				{hotel.name}</strong> you can select and insert your preferred competitors based on your location and
				star/key rating .
			</p>}
		<RadioGroup value={filter} onChange={setFilter} className="mt-4">
			<RadioGroup.Label className="sr-only">
				Filter Competitors
			</RadioGroup.Label>
			<div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
				{Object.values(TAB).map((option) => (
					<RadioGroup.Option
						key={option}
						value={option}
						className={({ checked }) =>
							`flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold cursor-pointer
          ${
								checked
									? 'bg-blue-600 text-white'
									: 'bg-white text-gray-900 hover:bg-gray-50'
							}
          ring-1 ring-gray-300`
						}
					>
						{_capitalize(option.toLowerCase())}
					</RadioGroup.Option>
				))}
			</div>
		</RadioGroup>
		{!!hotelSuggestionsOutput && !_isEmpty(hotelSuggestionsOutput.suggestions) && !isLoading &&
			<div className="mt-3 flow-root">
				<div className="overflow-x-auto overflow-y-auto max-h-[500px]">
					<div className="inline-block min-w-full p-2 align-middle">
						<table className="min-w-full divide-y divide-gray-300 text-gray-500">
							<thead>
							<tr>
								<th scope="col"
										className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"></th>
								<th scope="col"
										className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Name
								</th>
								<th scope="col"
										className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Stars
								</th>
								<th scope="col"
										className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Reviews
								</th>
								<th scope="col"
										className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Distance
								</th>
							</tr>
							</thead>

							{Object.values(TAB).map(tab => <tbody key={tab}
																										className={Utilities.classNames('divide-y divide-gray-200 bg-white', tab !== filter ? 'hidden' : '')}>
							{hotelSuggestionsOutput.suggestions
								.filter((item) => {
									if (tab === TAB.ALL) return true;
									if (tab === TAB.STARS) return item.hotel.ratingType === 'STARS';
									if (tab === TAB.KEYS) return item.hotel.ratingType !== 'STARS';
									return false;
								})
								.map((item) => (
									<tr key={item.hotel.id}>
										<td className="whitespace-nowrap py-3 pl-4 pr-3 sm:pl-0 text-sm">
											<input {...form.register('competitorIds')}
														 value={item.hotel.id}
														 type="checkbox"
														 className="input-checkbox" />
										</td>
										<td className="px-2 py-3 text-sm font-medium text-gray-900">
											{item.hotel.name}
										</td>
										<td className="whitespace-nowrap px-2 py-3 text-sm">
											<Rating rating={item.hotel.rating!} ratingType={item.hotel.ratingType!} />
										</td>

										<td className="whitespace-nowrap px-2 py-3 text-sm">{item.reviewScoreAverage}</td>
										<td className="whitespace-nowrap px-2 py-3 text-sm">{Utilities.formatDouble(item.distance / 1000)}km
										</td>
									</tr>))}

							</tbody>)}
						</table>
					</div>
				</div>
			</div>}
		{(!hotelSuggestionsOutput || _isEmpty(hotelSuggestionsOutput.suggestions)) && !isLoading && <div className="mt-4">
			<Message state="info">There are no appropriate competitors found near by!</Message>
		</div>}
		{isLoading && <div className="mt-4"><FormPlaceholder /></div>}

	</Modal>;
});

export default SetupPropertyHotelCompetitorSuggestionsModal;